import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from "@angular/forms";
import { MatSelect } from "@angular/material/select";
import { SelectOption } from "../../core/models/select-option.model";
import { StringHelper } from "../../core/helpers/string-helper";

@Component({
  selector: '[table-inline-cell-select]',
  templateUrl: './table-inline-cell-select.component.html',
  styleUrls: ['./table-inline-cell-select.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TableInlineCellSelectComponent implements AfterViewInit {
  @Input('items') items: SelectOption[] = [];
  @Input('parentForm') parentForm: UntypedFormGroup;
  @Input('controlName') controlName: string;
  @Input('inputPlaceholder') inputPlaceholder: string;
  @Input('minWidth') minWidth: string;
  @Input('maxWidth') maxWidth: string;
  @Input('displaySearch') displaySearch: boolean = false;
  @Input('filteredItems') filteredItems: SelectOption[] = [];
  @Input('editable') editable = false;
  @Input('maxLength') maxLength = 17;
  @Output('valueChanged') valueChanged = new EventEmitter();
  @Input('allowClear') allowClear: boolean = false;

  searchForm = new FormGroup({
    search: new FormControl<string>('')
  });

  @ViewChild(MatSelect) matSelect: MatSelect;

  panelOpened = false;

  private cachedValue: any;
  private cachedItems: SelectOption[];
  private cachedClosedItems: SelectOption[] = [];

  constructor(private cd: ChangeDetectorRef) { }

  // Render only the selected option while the panel is closed. mat-option components are
  // instantiated eagerly even when the panel never opens, so rendering full option lists in
  // every cell of a table multiplies into thousands of live components (rows x options).
  get visibleItems(): SelectOption[]
  {
    if (this.panelOpened)
    {
      return this.displaySearch ? this.filteredItems : this.items;
    }

    const value = this.parentForm.get(this.controlName).value;
    if (value !== this.cachedValue || this.items !== this.cachedItems)
    {
      this.cachedValue = value;
      this.cachedItems = this.items;
      const selectedItem = this.items.find(i => i.value === value);
      this.cachedClosedItems = selectedItem ? [selectedItem] : [];
    }

    return this.cachedClosedItems;
  }

  onOpenedChange(opened: boolean): void
  {
    this.panelOpened = opened;
  }

  // openedChange fires only after the open animation completes, which is too late — the panel
  // would briefly show just the selected option. Expanding on mousedown/keydown makes the full
  // list available before the panel renders.
  onTriggerInteraction(): void
  {
    this.panelOpened = true;
  }

  // Closed-panel keyboard interaction (arrow keys, type-ahead) can change the value without ever
  // opening the panel, so openedChange(false) never fires to reset panelOpened. Falling back to
  // blur catches that case without racing the open animation the way a timed resync would.
  onBlur(): void
  {
    if (!this.matSelect?.panelOpen)
    {
      this.panelOpened = false;
    }
  }

  ngAfterViewInit(): void {
    this.searchForm.controls.search.valueChanges.subscribe(value => this.filteredItems = this.items.filter(i => i.text?.toLowerCase().includes(value?.toLowerCase())));
    this.cd.detectChanges();
  }

  onClear(): void {
    this.parentForm.patchValue({[this.controlName]: null});
    this.valueChanged.emit({value: null});
  }

  onValueChanged(event: any) {
    this.valueChanged.emit(event);
  }

  getSelectionText(): string {
    const id = this.parentForm.get(this.controlName).value;
    if (id === null || id === '')
      return '';

    let text = this.items.find(i => i.value === id)?.text || ''
    if (text.length > this.maxLength) {
      return StringHelper.limit(text, this.maxLength);
    }

    return this.items.find(i => i.value === id)?.text || '';
  }
}
